import React, { useState, useEffect } from "react";
import { CustomerService } from "services/CustomerService";
import { toast } from "react-toastify";
import ApiKeySelect from "./common/ApiKeySelect";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";

interface SendEmailSidebarProps {
  customerId: string | number;
  // Key picked in the page's API Key dropdown, used as the starting choice.
  defaultApiKeyId?: string;
  onClose: () => void;
  onSend: () => void;
}

const SendEmailSidebar: React.FC<SendEmailSidebarProps> = ({ customerId, defaultApiKeyId = "", onClose, onSend }) => {
  const [apiKeyId, setApiKeyId] = useState(defaultApiKeyId);

  const [apiKey, setApiKey] = useState<string>("");
  const [formData, setFormData] = useState({
    To: "",
    Subject: "",
    HtmlContent: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Sending authenticates with the chosen key's value.
  useEffect(() => {
    setApiKey("");
    if (!apiKeyId) return;
    let cancelled = false;
    CustomerService.apiKey
      .getApiKeyById(parseInt(apiKeyId))
      .then((res) => {
        if (!cancelled && res.data?.key) setApiKey(res.data.key);
      })
      .catch((err) => console.error("Failed to fetch API key:", err));
    return () => {
      cancelled = true;
    };
  }, [apiKeyId]);

  const validateEmail = (email: string): boolean =>
    /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => { const { [name]: _, ...rest } = prev; return rest; });
    }
  };

  const handleQuillChange = (content: string) => {
    setFormData((prev) => ({ ...prev, HtmlContent: content }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateEmail(formData.To)) {
      setErrors({ To: "Please enter a valid email address" });
      return;
    }
    setIsSubmitting(true);
    try {
      await CustomerService.email.sendEmail(
        { To: [formData.To], Subject: formData.Subject, HtmlContent: formData.HtmlContent },
        apiKey,
      );
      toast.success("Email sent successfully!", { position: "top-right", autoClose: 3000 });
      onSend();
      onClose();
      setFormData({ To: "", Subject: "", HtmlContent: "" });
    } catch (err: any) {
      toast.error(err.response?.data || "Failed to send email", { position: "top-right", autoClose: 3000 });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <ApiKeySelect
        id="send-email-api-key"
        stacked
        label="Send from API key *"
        emptyLabel="Choose an API key"
        customerId={customerId}
        value={apiKeyId}
        onChange={setApiKeyId}
      />
      <div className="mb-3">
        <label htmlFor="To" className="form-label fw-bold">Recipient Email *</label>
        <input
          type="email"
          className={`form-control ${errors.To ? "is-invalid" : ""}`}
          id="To"
          name="To"
          value={formData.To}
          onChange={handleChange}
          required
          placeholder="Enter recipient email"
        />
        {errors.To && <div className="invalid-feedback">{errors.To}</div>}
      </div>

      <div className="mb-3">
        <label htmlFor="Subject" className="form-label fw-bold">Subject *</label>
        <input
          type="text"
          className="form-control"
          id="Subject"
          name="Subject"
          value={formData.Subject}
          onChange={handleChange}
          required
          placeholder="Enter email subject"
        />
      </div>

      <div className="mb-4">
        <label htmlFor="HtmlContent" className="form-label fw-bold">Email Body *</label>
        <div style={{ height: "300px" }}>
          <ReactQuill
            value={formData.HtmlContent}
            onChange={handleQuillChange}
            style={{ height: "250px" }}
            modules={{
              toolbar: [
                [{ header: [1, 2, 3, false] }],
                ["bold", "italic", "underline", "strike"],
                [{ list: "ordered" }, { list: "bullet" }],
                ["link"],
                ["clean"],
              ],
            }}
          />
        </div>
      </div>

      <div className="d-flex justify-content-end gap-2">
        <button
          type="button"
          className="btn btn-outline-secondary"
          onClick={onClose}
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || !apiKey}
        >
          {isSubmitting ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
              Sending...
            </>
          ) : "Send Email"}
        </button>
      </div>
    </form>
  );
};

export default SendEmailSidebar;
